import { getInvoice } from '@back-office/http';
import { GcpVerticalTabComponent, TransactionStatus } from '@back-office/ui';
import { useLocalStore } from 'mobx-react';
import { Instance } from 'mobx-state-tree';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import {
  useAsyncRetry,
  useInterval,
  useMount,
  useSearchParam,
  useUnmount,
} from 'react-use';
import useInject from '../../../hooks/useInject';
import InvoicesModel from '../../../stores/invoice.store';

type UseViewModelReturn = {
  state: {
    secondsPassed: number;
    delay: number;
    isPollingSuccess: boolean;
    isRunning: boolean;
    isLoading: boolean;
    handleOnCancel: () => void;
    handleOnClick: () => void;
    openAlert: (message: string) => void;
  };
  invoice: Instance<typeof InvoicesModel>;
  detail: string;
};

const useViewModel = (): UseViewModelReturn => {
  const history = useHistory();
  const { invoice, alert, setStage, setInvoice } = useInject((store) => store);
  const { t } = useTranslation();
  const txId = useSearchParam('txId') as string;
  const detail = decodeURIComponent(useSearchParam('detail') as string);

  const state = useLocalStore(() => ({
    secondsPassed: 0,
    delay: 2000,
    isPollingSuccess: false,
    isRunning: true,
    isLoading: false,
    handleOnCancel() {
      alert.closeDialog();
      setStage(GcpVerticalTabComponent.CreateQR);
      history.push('/');
    },
    handleOnClick() {
      setStage(GcpVerticalTabComponent.Transactions);
      history.push('/');
    },
    openAlert(message: string) {
      alert.openAlert({
        cancelBtn: 'dialog.cancel',
        okBtn: 'dialog.retry',
        handleCancel: state.handleOnCancel,
        handleOK() {
          alert.closeDialog();
          state.isRunning = true;
        },
        title: 'dialog.common_title',
        message,
      });
    },
  }));

  const polling = useAsyncRetry(async () => {
    try {
      const response = await getInvoice(txId);
      const { data, status } = response.data;
      if (status.code !== 1000) {
        throw new Error(status.messages[0]);
      }
      if (!invoice.txId) {
        setInvoice(data);
      }
      if (
        [
          TransactionStatus.Authorized,
          TransactionStatus.Captured,
          TransactionStatus.Cleared,
        ].includes(data.status as TransactionStatus)
      ) {
        state.isLoading = false;
        state.isRunning = false;
        state.isPollingSuccess = true;
      } else if (TransactionStatus.Pending === data.status) {
        state.isLoading = true;
      }
    } catch (e) {
      state.openAlert(e.message);
      state.isRunning = false;
    }
  });

  useInterval(
    () => {
      state.secondsPassed += 1;
      polling.retry();
      if (state.secondsPassed === 60 || state.isPollingSuccess) {
        state.secondsPassed = 0;
        state.isRunning = false;
        state.openAlert(t('dialog.timeout'));
      }
    },
    state.isRunning ? state.delay : null
  );

  useMount(() => {
    if (!txId || !detail) {
      history.push('/');
    }
  });

  useUnmount(() => {
    invoice.reset();
  });

  return {
    state,
    invoice,
    detail,
  };
};

export default useViewModel;
